import React from "react";
import { DatePicker } from "antd";
import { Controller, useFormContext } from "react-hook-form";
import type { Dayjs } from "dayjs";
import dayjs from "dayjs";

import { DisabledTimes } from "rc-picker/lib/interface";


interface DatePickerFieldProps {
  label: string;
  name: string;
  required?: boolean;
  minDate?: Dayjs;
  maxDate?: Dayjs;
  disabled?: boolean | [boolean, boolean];
  top?: boolean;
  suppressPastDateWarning?: boolean;
}

const range = (start: number, end: number) => new Array(end - start).fill(0).map((_, i) => i + start);
const timeFormat = "HH:mm"
export const dateFormat = "DD.MM.YYYY"
export const dateTimeFormat = `${dateFormat} ${timeFormat}`

const DatePickerField: React.FC<DatePickerFieldProps> = ({
                                                           label,
                                                           name,
                                                           required,
                                                           disabled,
                                                           minDate,
                                                           maxDate,
                                                           top,
                                                           suppressPastDateWarning
                                                         }) => {
  const form = useFormContext();
  const rules = required
    ? { validate: (value: any) => !!value?.startDate && !!value?.endDate }
    : {};
  const watched = (form as any).watch ? (form as any).watch(name) : undefined;
  const hasValue = !!watched?.startDate && !!watched?.endDate;
  const showRequired = !!required && (!hasValue || !!form.formState.errors?.[name])

  // Check if start date is disabled (either boolean true or array [true, ...])
  const isStartDateDisabled = disabled === true || (Array.isArray(disabled) && disabled[0] === true);

  // Check if start date is in the past (only show warning if start date is not disabled and not suppressed)
  const isStartDateInPast = !isStartDateDisabled && !suppressPastDateWarning && watched?.startDate && dayjs(watched.startDate).isBefore(dayjs(), 'minute');

  return (
    <div
      className={`sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start py-5 ${
        top ? "pt-0" : "sm:border-t"
      } sm:border-gray-200`}
    >
      <label className="block text-sm font-medium text-gray-700 pt-2">
        {label}{showRequired && <span className="text-red-500 ml-1">*</span>}
        {isStartDateInPast && <div className="text-red-500 text-xs mt-1">Start date cannot be in the past</div>}
      </label>
      <Controller
        name={name}
        control={form.control}
        rules={rules}
        render={({ field: { onChange, value } }) => (
          <DatePicker.RangePicker
            value={value && [dayjs(value.startDate), dayjs(value.endDate)]}
            className={`max-w-lg w-[400px] shadow-sm sm:max-w-xs h-[38px] focus:ring-indigo-500 focus:border-indigo-500 rounded-md ${
              isStartDateInPast ? 'border-red-500 border-2' : 'border-gray-300'
            }`}
            allowClear={false}
            format={dateTimeFormat}
            disabled={disabled}
            showTime={{
              format: timeFormat,
              defaultValue: [dayjs("00:01", timeFormat), dayjs("23:59", timeFormat)]
            }}
            minDate={minDate}
            maxDate={maxDate}
            disabledTime={(now, type) => {
              const disabledTime: DisabledTimes = {}

              if (type === 'start' && minDate && now?.isSame(minDate, 'day')) {
                const minHour = minDate?.hour()
                disabledTime.disabledHours = () => range(0, minDate.hour())
                disabledTime.disabledMinutes = (hour) => hour === minHour ? range(0, minDate.minute()): []
              }
              if (type === 'end' && maxDate && now?.isSame(maxDate, 'day')) {
                const maxHour = maxDate?.hour()
                disabledTime.disabledHours = () => range(maxHour + 1, 24)
                disabledTime.disabledMinutes = (hour) => hour === maxHour ? range(maxDate.minute() + 1, 60): []
              }

              return disabledTime
            }}
            onCalendarChange={([startDate, endDate]) => {
              if (startDate && endDate) {
                onChange({
                  startDate: startDate.toDate(),
                  endDate: endDate.toDate()
                })
              }
            }}
          />
        )}
      />
    </div>
  );
};

export default DatePickerField;
